import "./App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { PlayerProvider } from "./context/PlayerContext";
import { CopyProvider } from "./context/CopyContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import { DownloadProvider } from "./context/DownloadContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import AnnouncementToasts from "./components/AnnouncementToasts";
import MediaPlayer from "./components/MediaPlayer";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";

import Home from "./pages/Home";
// Every page except the landing page loads its code only when it is opened,
// so the first visit downloads far less JavaScript.
const Audios = lazy(() => import("./pages/Audios"));
const AudioDetail = lazy(() => import("./pages/AudioDetail"));
const Scenepacks = lazy(() => import("./pages/Scenepacks"));
const ScenepackDetail = lazy(() => import("./pages/ScenepackDetail"));
const Edits = lazy(() => import("./pages/Edits"));
const EditsSoon = lazy(() => import("./pages/EditsSoon"));
const AAPlus = lazy(() => import("./pages/AAPlus"));
const Contact = lazy(() => import("./pages/Contact"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const CreatorProfile = lazy(() => import("./pages/CreatorProfile"));
const Creators = lazy(() => import("./pages/Creators"));
const Requests = lazy(() => import("./pages/Requests"));
const Franchise = lazy(() => import("./pages/Franchise"));
const Celebrities = lazy(() => import("./pages/Celebrities"));
const Person = lazy(() => import("./pages/Person"));
const MoviePage = lazy(() => import("./pages/MoviePage"));
const Profile = lazy(() => import("./pages/Profile"));
const Search = lazy(() => import("./pages/Search"));
const Dmca = lazy(() => import("./pages/Dmca"));

import { EDITS_LIVE } from "./lib/features";

// While the browser is idle, fetch the code of the pages people open next, so tapping a link is instant.
function Prefetch() {
  useEffect(() => {
    if (navigator.connection?.saveData) return undefined;
    const run = () => {
      import("./pages/Audios");
      import("./pages/Scenepacks");
      import("./pages/MoviePage");
      import("wavesurfer.js");
    };
    if (window.requestIdleCallback) {
      const id = window.requestIdleCallback(run, { timeout: 5000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const id = setTimeout(run, 3000);
    return () => clearTimeout(id);
  }, []);
  return null;
}

function PageLoading() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loading">
      <div className="w-8 h-8 rounded-full border-2 border-white/15 border-t-[#880015] animate-spin" />
    </div>
  );
}

// on phones toasts sit at the bottom (above the player); the top is for announcements
function ResponsiveToaster() {
  const [phone, setPhone] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setPhone(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return <Toaster theme="dark" position={phone ? "bottom-center" : "bottom-right"} offset={phone ? 120 : undefined} />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <CopyProvider>
            <FavoritesProvider>
            <DownloadProvider>
            <PlayerProvider>
            <Prefetch />
            <Navbar />
            <AnnouncementToasts />
            <main className="pt-20 pb-32 min-h-[100dvh]">
              <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/audios" element={<Audios />} />
                <Route path="/audios/:slug" element={<AudioDetail />} />
                <Route path="/search" element={<Search />} />
                <Route path="/scenepacks" element={<Scenepacks />} />
                <Route path="/marvel" element={<Franchise slug="marvel" />} />
                <Route path="/celebrities" element={<Celebrities />} />
                <Route path="/people" element={<Navigate to="/celebrities" replace />} />
                <Route path="/people/:slug" element={<Person />} />
                <Route path="/scenepacks/request" element={<Requests />} />
                <Route path="/scenepacks/movie/:slug" element={<MoviePage />} />
                <Route path="/scenepacks/:id" element={<ScenepackDetail />} />
                <Route path="/requests" element={<Requests />} />
                <Route path="/edits" element={EDITS_LIVE ? <Edits /> : <EditsSoon />} />
                <Route path="/aa-plus" element={<AAPlus />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/dmca" element={<Dmca />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/creators" element={<Creators />} />
                <Route path="/creators/:handle" element={<CreatorProfile />} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute role="creator"><Dashboard /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute role="admin"><Admin /></ProtectedRoute>} />
              </Routes>
              </Suspense>
            </main>
            <Footer />
            <MediaPlayer />
            <ResponsiveToaster />
          </PlayerProvider>
            </DownloadProvider>
            </FavoritesProvider>
          </CopyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
